/**
 * Isometric (2:1 dimetric) math shared by all iso scenes.
 *
 * Convention: every object is a "box" whose top face is authored in flat 2D
 * local coordinates (w × h) and projected with a single matrix transform —
 * matrix(SQ3, 0.5, -SQ3, 0.5, tx, ty) — so card content (text, mini-UI) needs
 * no manual projection. (tx, ty) is the screen position of the top face's
 * TOP vertex; local +x runs down-right, local +y runs down-left.
 *
 * The exploded-stack animation only ever translates screen y (z maps to −y in
 * this projection), so isometric alignment can never drift while animating.
 */

export const SQ3 = Math.sqrt(3) / 2; // 0.866…
/** Screen slope (dy/dx) of a segment that runs along an isometric axis. */
export const SLOPE = 0.5 / SQ3; // 0.577…

export type Box = { tx: number; ty: number; w: number; h: number; depth: number };
export type Pt = { x: number; y: number };

/** Screen position of local point (u, v) on a box's top face. */
export const pt = (b: Box, u: number, v: number): Pt => ({
  x: b.tx + SQ3 * (u - v),
  y: b.ty + 0.5 * (u + v),
});

export const topV = (b: Box): Pt => pt(b, 0, 0);
export const rightV = (b: Box): Pt => pt(b, b.w, 0);
export const leftV = (b: Box): Pt => pt(b, 0, b.h);
export const bottomV = (b: Box): Pt => pt(b, b.w, b.h);

/** SVG transform that projects flat 2D content onto the box's top face. */
export const isoMatrix = (b: Box): string => `matrix(${SQ3},0.5,${-SQ3},0.5,${b.tx},${b.ty})`;

export const lerp = (a: Pt, b: Pt, t: number): Pt => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

/** Path-data point formatter (0.1px precision) — shared by scene configs
 *  building custom connector paths; don't copy it locally (#224). */
export const P = (p: Pt) => `${Math.round(p.x * 10) / 10} ${Math.round(p.y * 10) / 10}`;

/**
 * Ground-plane elbow between two points: decomposes the screen delta into the
 * two isometric ground axes A=(SQ3, 0.5) and B=(−SQ3, 0.5), so both segments
 * run along true iso directions (a straight "circuit trace" on the floor).
 */
export const groundElbow = (p1: Pt, p2: Pt): string => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const a = dy + dx / (2 * SQ3); // p2 = p1 + a·A + b·B
  const mid = { x: p1.x + SQ3 * a, y: p1.y + 0.5 * a };
  return `M ${P(p1)} L ${P(mid)} L ${P(p2)}`;
};

export type ConnectorSpec = {
  id: string;
  d: string;
  /** Screen point for the little terminal node dot at the exposed end. */
  node: Pt;
};
